import { z } from 'zod';

/**
 * Zod schemas for the BP-05 §5 responses (ARCHITECTURE §3 Validation: "Pydantic on the
 * api; Zod on the web from the same OpenAPI").
 *
 * Hand-written for the reason `features/scheduling/schemas.ts` gives: `lib/api/schema.d.ts`
 * is regenerated in CI and a screen that imported it would compile against whatever was
 * last exported, not against the contract this feature was written to.
 *
 * **No client PII crosses this boundary.** A session carries `client_id`; the one screen
 * that shows a person shows the api's own `phone_masked` (§9), and there is no branch here
 * that unmasks it — the value never arrives.
 *
 * Money is integer paise and quantities are **decimal strings**: a BOM line of `0.8 ml`
 * read into a JavaScript number and written back would not round-trip, so the wire value
 * is carried verbatim and parsed only where it is displayed or compared.
 */

/** §3's `service_session.status`, in the order R1 walks them. */
export const sessionStatuses = [
  'ready',
  'in_progress',
  'paused',
  'completed',
  'abandoned',
] as const;

/** §3's nine `session_step.type` values — §6's "giant step buttons (9 types)". */
export const stepTypes = [
  'prep',
  'removal',
  'base',
  'colour',
  'art',
  'top',
  'cure',
  'dry',
  'other',
] as const;

export const staffRoles = ['primary', 'assist'] as const;
export const materialSources = ['bom', 'manual'] as const;
export const noteKinds = ['tech', 'client_visible'] as const;
export const commissionScopes = ['service', 'category', 'product', 'all'] as const;
export const commissionAppliesTo = ['staff', 'role'] as const;
export const commissionBases = ['percent_net', 'fixed_per_unit', 'tiered_monthly'] as const;
export const splitModes = ['by_minutes', 'fixed'] as const;
export const commissionKinds = ['earn', 'reversal'] as const;

export const sessionStatusSchema = z.enum(sessionStatuses);
export const stepTypeSchema = z.enum(stepTypes);
export const staffRoleSchema = z.enum(staffRoles);
export const commissionBasisSchema = z.enum(commissionBases);
export const splitModeSchema = z.enum(splitModes);

/** `GET /time` (R5): the server's clock, the only one a duration may be measured against. */
export const timeSchema = z.object({ now: z.string() });

/** One worked step (§5 `StepOut`). `ended_at` is null while the clock runs. */
export const stepSchema = z.object({
  id: z.string(),
  type: stepTypeSchema,
  label: z.string().nullable(),
  staff_id: z.string(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
  seq: z.number(),
});

/** One person's span on the session (§5 `StaffSpanOut`); `to_at` null is "still on it". */
export const staffSpanSchema = z.object({
  id: z.string(),
  staff_id: z.string(),
  role: staffRoleSchema,
  from_at: z.string(),
  to_at: z.string().nullable(),
  handed_from_staff_id: z.string().nullable(),
  reason: z.string().nullable(),
});

/** One material line (§5 `MaterialOut`). Quantities are decimal strings (R7). */
export const materialSchema = z.object({
  id: z.string(),
  sku_id: z.string(),
  qty: z.string(),
  unit: z.string(),
  source: z.enum(materialSources),
  bom_qty: z.string().nullable(),
  variance_qty: z.string().nullable(),
  step_type: stepTypeSchema.nullable(),
  stock_movement_id: z.string().nullable(),
  removed: z.boolean(),
  sell: z.boolean(),
  sku_withdrawn_at_copy: z.boolean(),
});

/** One note (§5 `NoteOut`). `body` is null once BP-03's erasure has run over it (§9). */
export const noteSchema = z.object({
  id: z.string(),
  staff_id: z.string(),
  body: z.string().nullable(),
  kind: z.enum(noteKinds),
  created_at: z.string(),
});

/** The queue row (§5 `ServiceSessionOut`). */
export const sessionSchema = z.object({
  id: z.string(),
  booking_id: z.string().nullable(),
  booking_item_id: z.string().nullable(),
  client_id: z.string().nullable(),
  outlet_id: z.string(),
  station_id: z.string().nullable(),
  service_id: z.string(),
  addon_ids: z.array(z.string()),
  status: sessionStatusSchema,
  primary_staff_id: z.string(),
  started_at: z.string().nullable(),
  ended_at: z.string().nullable(),
  paused_ms: z.number(),
  active_ms: z.number().nullable(),
  invoice_id: z.string().nullable(),
  abandon_reason: z.string().nullable(),
  repeat_of_session_id: z.string().nullable(),
  version: z.number(),
});

/**
 * The timer screen's whole state in one response (§5 `SessionDetailOut`).
 *
 * `server_now` rides along with every write, which is what lets the clock re-measure its
 * drift on each answer instead of trusting the tablet (R5).
 */
export const sessionDetailSchema = sessionSchema.extend({
  steps: z.array(stepSchema).default([]),
  staff: z.array(staffSpanSchema).default([]),
  materials: z.array(materialSchema).default([]),
  notes: z.array(noteSchema).default([]),
  allowed_targets: z.array(sessionStatusSchema).default([]),
  server_now: z.string(),
  warnings: z.array(z.string()).default([]),
});

export const sessionPageSchema = z.object({
  items: z.array(sessionSchema),
  next_cursor: z.string().nullable().optional(),
});

/** One row of the audit trail (§5 `SessionEventOut`, §13's "event log tab"). */
export const eventSchema = z.object({
  seq: z.number(),
  type: z.string(),
  at: z.string(),
  actor_id: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
});

/** One of R12's "last 3 sets" (§5 `PreviousSetOut`). */
export const previousSetSchema = z.object({
  session_id: z.string(),
  service_id: z.string(),
  ended_at: z.string().nullable(),
  materials: z.array(materialSchema).default([]),
  step_minutes_by_type: z.record(z.string(), z.number()).default({}),
  notes: z.array(noteSchema).default([]),
});

/** R12's nail profile — masked, which is all this feature ever sees. */
export const clientProfileSchema = z.object({
  client_id: z.string().nullable(),
  phone_masked: z.string().nullable(),
  shape: z.string().nullable(),
  length: z.string().nullable(),
  finish_pref: z.string().nullable(),
  allergies: z.array(z.string()).default([]),
  sensitivities: z.string().nullable(),
  technician_pref: z.string().nullable(),
  last_sets: z.array(previousSetSchema).default([]),
});

/** One tier of R17's monthly ladder. */
export const commissionTierSchema = z.object({
  from_paise: z.number(),
  percent_bp: z.number(),
});

/** One rule (§5 `CommissionRuleOut`, R15–R17). */
export const commissionRuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.enum(commissionScopes),
  scope_id: z.string().nullable(),
  applies_to: z.enum(commissionAppliesTo),
  staff_id: z.string().nullable(),
  role: z.string().nullable(),
  basis: commissionBasisSchema,
  percent_bp: z.number().nullable(),
  fixed_paise: z.number().nullable(),
  tiers: z.array(commissionTierSchema).nullable(),
  split_mode: splitModeSchema,
  primary_share_bp: z.number().nullable(),
  priority: z.number(),
  valid_from: z.string(),
  valid_to: z.string().nullable(),
  active: z.boolean(),
});

/** One line of a dry run (§5 `CommissionPreviewOut`). `rule_id` null means nobody earns. */
export const commissionPreviewSchema = z.object({
  staff_id: z.string(),
  invoice_line_id: z.string(),
  rule_id: z.string().nullable(),
  rule_name: z.string().nullable(),
  basis: commissionBasisSchema.nullable(),
  base_amount_paise: z.number(),
  share_bp: z.number(),
  amount_paise: z.number(),
  explain: z.record(z.string(), z.unknown()).default({}),
});

/** One earned or reversed row of a statement (§5 `StatementLineOut`, R20). */
export const statementLineSchema = z.object({
  period: z.string(),
  staff_id: z.string(),
  invoice_id: z.string(),
  invoice_line_id: z.string(),
  session_id: z.string().nullable(),
  rule_name: z.string().nullable(),
  basis: commissionBasisSchema,
  base_amount_paise: z.number(),
  share_bp: z.number(),
  amount_paise: z.number(),
  kind: z.enum(commissionKinds),
  computed_at: z.string(),
  explain: z.record(z.string(), z.unknown()).default({}),
});

export const statementSchema = z.object({
  period: z.string(),
  staff_id: z.string(),
  earn_paise: z.number(),
  reversal_paise: z.number(),
  net_paise: z.number(),
  lines: z.array(statementLineSchema).default([]),
});

export type ServerTime = z.infer<typeof timeSchema>;
export type SessionStatus = (typeof sessionStatuses)[number];
export type StepType = (typeof stepTypes)[number];
export type StaffRole = (typeof staffRoles)[number];
export type NoteKind = (typeof noteKinds)[number];
export type Step = z.infer<typeof stepSchema>;
export type StaffSpan = z.infer<typeof staffSpanSchema>;
export type Material = z.infer<typeof materialSchema>;
export type Note = z.infer<typeof noteSchema>;
export type Session = z.infer<typeof sessionSchema>;
export type SessionDetail = z.infer<typeof sessionDetailSchema>;
export type SessionPage = z.infer<typeof sessionPageSchema>;
export type SessionEvent = z.infer<typeof eventSchema>;
export type PreviousSet = z.infer<typeof previousSetSchema>;
export type ClientProfile = z.infer<typeof clientProfileSchema>;
export type CommissionTier = z.infer<typeof commissionTierSchema>;
export type CommissionRule = z.infer<typeof commissionRuleSchema>;
export type CommissionPreview = z.infer<typeof commissionPreviewSchema>;
export type StatementLine = z.infer<typeof statementLineSchema>;
export type Statement = z.infer<typeof statementSchema>;
export type CommissionScope = (typeof commissionScopes)[number];
export type CommissionAppliesTo = (typeof commissionAppliesTo)[number];
export type CommissionBasis = (typeof commissionBases)[number];
export type SplitMode = (typeof splitModes)[number];
